import { Button, Container } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Adsense from '../Ads/Adsense';
import { formatManwon, formatKRW } from '../../utils/formatKRW';
import { useRetirementStore } from '../../store/retirementStore';
import { logEvent } from '../../utils/ga';
import { ExpertCallout } from '../shared/ExpertCallout';
import { calculateCompound, CompoundOptions, CompoundResult } from './CompoundPage.hooks';
import { CompoundForm } from './Form/CompoundForm';
import CompoundTable from './Table/CompoundTable';
import styles from './CompoundPage.module.css';

function getExpertCallout(result: CompoundResult, options: CompoundOptions) {
  if (result.profitRatePct >= 100) {
    return {
      title: '복리 효과가 실현되고 있습니다',
      body: "수익이 납입 원금을 넘어섰습니다. 이 시점부터는 '내가 넣은 돈'보다 '돈이 번 돈'이 더 많다는 뜻입니다.",
      cta: { label: '이 금액으로 노후 시뮬레이션', href: '/retirement' },
    };
  }
  if (options.period < 10) {
    return {
      title: '기간이 짧을수록 납입액이 더 중요합니다',
      body: '10년 미만 구간에서는 복리 효과가 본격화되기 전입니다. 수익률보다 월 납입액을 높이는 것이 더 효과적입니다. 기간을 15~20년으로 늘려보세요.',
    };
  }
  if (options.monthlyContribution === 0) {
    return {
      title: '정기 납입을 더하면 결과가 크게 달라집니다',
      body: '현재는 초기 투자금만으로 계산 중입니다. 월 30만원만 추가 납입해도 최종 자산이 크게 증가합니다.',
    };
  }
  return null;
}

function CompoundPage() {
  const navigate = useNavigate();
  const setCompoundFinalAmount = useRetirementStore((s) => s.setCompoundFinalAmount);
  const [result, setResult] = useState<CompoundResult>();
  const [options, setOptions] = useState<CompoundOptions>();
  const [tableOpen, setTableOpen] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  const handleSubmit = useCallback((opts: CompoundOptions) => {
    logEvent('calculator', 'compound_calculate', 'submit');
    setResult(calculateCompound(opts));
    setOptions(opts);
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  }, []);

  const callout = useMemo(
    () => (result && options ? getExpertCallout(result, options) : null),
    [result, options]
  );

  const handleRetirementCTA = () => {
    if (!result) return;
    logEvent('calculator', 'compound_result_cta_click', '/retirement');
    setCompoundFinalAmount(result.finalAsset);
    navigate(`/retirement?asset=${result.finalAsset}`);
  };

  return (
    <div className={styles.root}>
      <Helmet>
        <title>복리 시뮬레이터 — 투자 기간별 자산 성장 계산 | 노후든든</title>
        <meta
          name="description"
          content="초기 투자금, 월 납입액, 연 수익률을 입력해 복리 효과를 연도별로 확인하세요. 수익이 원금을 넘어서는 전환점을 직접 시뮬레이션합니다."
        />
      </Helmet>

      <Container maxWidth="sm">
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>복리 시뮬레이터</h1>
          <p className={styles.pageDesc}>
            노후 준비를 위한 장기 투자를 시뮬레이션합니다. 지금 쌓는 작은 습관이 은퇴 날 얼마가 될지 확인하세요.
          </p>
        </div>
        <div className={styles.formCard}>
          <CompoundForm className={styles.form} onSubmit={handleSubmit} />
        </div>
      </Container>

      {result && options && (
        <div ref={resultRef}>
          <Container maxWidth="lg" sx={{ mt: 4 }}>
            <div className={styles.resultSection}>
              {/* Hero */}
              <div className={styles.resultHeroCard}>
                <p className={styles.resultHeroLabel}>
                  {options.period}년 후 예상 최종 자산
                </p>
                <p className={styles.resultHeroAmount}>{formatManwon(result.finalAsset)}</p>
                <span className={styles.resultHeroBadge}>+{result.profitRatePct}% 수익</span>
              </div>

              {/* Metrics */}
              <div className={styles.metricsRow}>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>총 납입 원금</p>
                  <p className={styles.metricValue}>{formatManwon(result.totalPrincipal)}</p>
                  <p className={styles.metricInterpretation}>내가 직접 넣은 돈</p>
                </div>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>복리 수익</p>
                  <p className={styles.metricValue}>{formatManwon(result.totalInterest)}</p>
                  <p className={styles.metricInterpretation}>돈이 번 돈</p>
                </div>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>수익 배율</p>
                  <p className={styles.metricValue}>
                    {result.totalPrincipal > 0
                      ? `${(result.finalAsset / result.totalPrincipal).toFixed(1)}배`
                      : '—'}
                  </p>
                  <p className={styles.metricInterpretation}>원금 대비 최종 자산</p>
                </div>
              </div>

              {/* Charts */}
              <div className={styles.chartsGrid}>
                <div className={styles.chartCard}>
                  <p className={styles.chartTitle}>연도별 자산 성장</p>
                  <div className={styles.areaChartWrapper}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={result.dataPoints} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                        <defs>
                          <linearGradient id="principal" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0.05} />
                          </linearGradient>
                          <linearGradient id="total" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.05} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis
                          dataKey="year"
                          tickFormatter={(v) => `${v}년`}
                          axisLine={false}
                          tickLine={false}
                          style={{ fontSize: '0.75rem' }}
                        />
                        <YAxis
                          tickFormatter={(v) => formatManwon(v)}
                          axisLine={false}
                          tickLine={false}
                          style={{ fontSize: '0.6875rem' }}
                          width={72}
                        />
                        <Tooltip<number, string>
                          formatter={((v: number, name: string) => [
                            formatKRW(v),
                            name === 'principal' ? '납입 원금' : '총 자산',
                          ]) as any}
                          labelFormatter={(l) => `${l}년`}
                        />
                        <Area
                          type="monotone"
                          dataKey="principal"
                          stroke="#6366f1"
                          strokeWidth={2}
                          fill="url(#principal)"
                        />
                        <Area
                          type="monotone"
                          dataKey="totalAsset"
                          stroke="#10b981"
                          strokeWidth={2}
                          fill="url(#total)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className={styles.chartCard}>
                  <p className={styles.chartTitle}>원금 vs 복리 수익</p>
                  <div className={styles.pieChartWrapper}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: '납입 원금', value: result.totalPrincipal },
                            { name: '복리 수익', value: Math.max(0, result.totalInterest) },
                          ]}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={90}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          <Cell fill="#6366f1" />
                          <Cell fill="#10b981" />
                        </Pie>
                        <Tooltip formatter={((v: number) => formatKRW(v)) as any} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Expert callout */}
              {callout && (
                <ExpertCallout
                  title={callout.title}
                  body={callout.body}
                  cta={callout.cta}
                />
              )}

              {/* Yearly table (collapsible) */}
              <div className={styles.tableToggle}>
                <button
                  className={styles.tableToggleBtn}
                  onClick={() => {
                    logEvent('calculator', tableOpen ? 'compound_detail_table_close' : 'compound_detail_table_open', 'result');
                    setTableOpen((v) => !v);
                  }}
                >
                  {tableOpen ? '▲ 연도별 상세 표 닫기' : '▼ 연도별 상세 표 보기'}
                </button>
                {tableOpen && (
                  <CompoundTable histories={result.dataPoints} />
                )}
              </div>

              {/* CTA */}
              <div className={styles.ctaRow}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleRetirementCTA}
                  sx={{ background: '#000033', '&:hover': { background: '#001166' } }}
                >
                  이 금액으로 노후 시뮬레이션 하기 →
                </Button>
              </div>

              <Adsense adClient="ca-pub-9359050149154028" adSlot="3275566807" />

              <p className={styles.disclaimer}>
                이 계산기는 투자 권유가 아닌 참고용 시뮬레이션입니다. 실제 수익은 시장 상황에 따라 다르며,
                세금·수수료·물가 상승률은 반영되지 않았습니다. 중요한 재무 결정은 전문가와 상담하세요.
              </p>
            </div>
          </Container>
        </div>
      )}
    </div>
  );
}

export default CompoundPage;
